import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { getMemoryAnalytics } from '../../services/memoryCategoriesService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Database,
  Archive,
  TrendingUp,
  AlertCircle,
  Star,
  Brain,
  RefreshCw
} from 'lucide-react';

const MemoryAnalytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const isSuperAdmin = user?.role === 'superadmin' || user?.is_superadmin;

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const data = await getMemoryAnalytics(period);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load memory analytics');
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const domainChartData = analytics?.domains_breakdown
    ? Object.entries(analytics.domains_breakdown).map(([domain, data]) => ({
        domain: domain.charAt(0).toUpperCase() + domain.slice(1).replace('_', ' '),
        count: data.count,
        avg_importance: data.avg_importance,
        concerns: data.concerns
      }))
    : [];

  const severityChartData = analytics?.severity_breakdown
    ? Object.entries(analytics.severity_breakdown).map(([severity, count]) => ({
        severity: severity.charAt(0).toUpperCase() + severity.slice(1),
        count
      }))
    : [];

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6b7280'];

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No analytics data available</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Memory Analytics</h1>
          <p className="text-gray-600">
            Comprehensive analytics for memory categories and milestone matching
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.total_messages || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Archived</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">
              {analytics.total_archived || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Importance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {(analytics.avg_importance_score || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Milestone Matches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {analytics.milestone_analytics?.total_milestone_matches || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Domain Breakdown Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Domain Breakdown</CardTitle>
            <CardDescription>Messages by ASQ domain</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={domainChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="domain" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#10b981" name="Messages" />
                <Bar dataKey="concerns" fill="#ef4444" name="Concerns" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Severity Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Severity Distribution</CardTitle>
            <CardDescription>Messages by severity level</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={severityChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ severity, count }) => `${severity}: ${count}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {severityChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Milestone Analytics */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Milestone Analytics
          </CardTitle>
          <CardDescription>
            ASQ milestone matching statistics and insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded">
              <div className="text-2xl font-bold text-blue-600">
                {analytics.milestone_analytics?.total_milestone_matches || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Total Matches</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded">
              <div className="text-2xl font-bold text-green-600">
                {(analytics.milestone_analytics?.avg_similarity_score || 0).toFixed(2)}
              </div>
              <div className="text-sm text-gray-600 mt-1">Avg Similarity</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded">
              <div className="text-2xl font-bold text-red-600">
                {analytics.milestone_analytics?.age_appropriate_concerns || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Age-Appropriate Concerns</div>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded">
              <div className="text-2xl font-bold text-emerald-600">
                {analytics.milestone_analytics?.achievements_celebrated || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Achievements</div>
            </div>
          </div>

          {analytics.milestone_analytics?.top_matched_milestones?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Top Matched Milestones</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Milestone</th>
                      <th className="text-right p-2">Matches</th>
                      <th className="text-right p-2">Concerns</th>
                      <th className="text-right p-2">Achievements</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.milestone_analytics.top_matched_milestones.map((milestone, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          <div className="font-medium">{milestone.name}</div>
                          <div className="text-xs text-gray-500">ID: {milestone.id}</div>
                        </td>
                        <td className="text-right p-2">{milestone.matches}</td>
                        <td className="text-right p-2 text-red-600">{milestone.concerns}</td>
                        <td className="text-right p-2 text-green-600">{milestone.achievements}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Concerns */}
      {analytics.top_concerns?.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Top Concerns
            </CardTitle>
            <CardDescription>
              Most frequently matched milestones with high severity concerns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.top_concerns.map((concern, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-200">
                  <div>
                    <div className="font-medium">{concern.milestone}</div>
                    <div className="text-xs text-gray-500">Milestone ID: {concern.milestone_id}</div>
                  </div>
                  <div className="text-lg font-bold text-red-600">{concern.count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Archival Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Archival Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded">
              <div className="text-sm text-gray-600 mb-1">Archived (Last 7 days)</div>
              <div className="text-2xl font-bold">
                {analytics.archival_stats?.archived_last_7_days || 0}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <div className="text-sm text-gray-600 mb-1">Restored (Last 7 days)</div>
              <div className="text-2xl font-bold text-green-600">
                {analytics.archival_stats?.restored_last_7_days || 0}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <div className="text-sm text-gray-600 mb-1">Archive Reasons</div>
              <div className="space-y-1 mt-2">
                {analytics.archival_stats?.archive_reasons &&
                  Object.entries(analytics.archival_stats.archive_reasons).map(([reason, count]) => (
                    <div key={reason} className="flex justify-between text-sm">
                      <span className="capitalize">{reason.replace('_', ' ')}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemoryAnalytics;

